import { Globe, Info, Mail, MapPin, Phone, Printer, Star } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { EnrichmentHandle } from '@/services/types/treatment-types';

import { EnrichmentSourcesIndicator } from './EnrichmentSourcesIndicator';

interface EnrichmentHandleCardProps {
  /** The enrichment handle to display */
  handle: EnrichmentHandle;
  /** Whether to show sources inline or as compact badge */
  showSources?: boolean;
  /** Compact display mode */
  compact?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Get the icon component for a handle type.
 */
function getHandleIcon(type: string) {
  switch (type.toUpperCase()) {
    case 'PHONE':
      return Phone;
    case 'FAX':
      return Printer;
    case 'EMAIL':
      return Mail;
    case 'MAILING_ADDRESS':
      return MapPin;
    case 'WEBSITE':
      return Globe;
    default:
      return Info;
  }
}

/**
 * Get the icon color class for a handle type.
 */
function getHandleIconColor(type: string) {
  switch (type.toUpperCase()) {
    case 'PHONE':
      return 'text-blue-600';
    case 'FAX':
      return 'text-purple-600';
    case 'EMAIL':
      return 'text-teal-600';
    case 'MAILING_ADDRESS':
      return 'text-amber-600';
    case 'WEBSITE':
      return 'text-indigo-600';
    default:
      return 'text-zinc-500';
  }
}

/**
 * Format the display value for an enrichment handle.
 */
function formatHandleValue(handle: EnrichmentHandle): string {
  if (handle.handle_type === 'MAILING_ADDRESS') {
    const parts = [
      handle.address_line1,
      handle.address_line2,
      [handle.city, handle.state, handle.zip].filter(Boolean).join(', '),
    ].filter(Boolean);
    return parts.join(', ') || handle.value || 'No address';
  }
  return handle.value || 'No value';
}

/**
 * Displays an enrichment handle with preferred indicator and sources.
 */
export const EnrichmentHandleCard = ({
  handle,
  showSources = true,
  compact = false,
  className,
}: EnrichmentHandleCardProps) => {
  const Icon = getHandleIcon(handle.handle_type);
  const iconColorClass = getHandleIconColor(handle.handle_type);
  const displayValue = formatHandleValue(handle);

  return (
    <div
      className={cn(
        'border rounded-lg transition-colors',
        handle.preferred
          ? 'border-amber-200 bg-amber-50/30'
          : 'border-zinc-200 bg-white',
        compact ? 'px-3 py-2' : 'px-4 py-3',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={cn('h-5 w-5', iconColorClass)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header row with preferred badge */}
          {handle.preferred && (
            <div className="flex items-center gap-1.5 mb-1">
              <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-200" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                Preferred for Records
              </span>
            </div>
          )}

          {/* Name and value */}
          <div className="flex items-center gap-2 flex-wrap">
            {handle.name && (
              <span
                className={cn(
                  'font-medium text-zinc-900',
                  compact ? 'text-sm' : 'text-base',
                )}
              >
                {handle.name}
              </span>
            )}
          </div>

          <div
            className={cn(
              'text-zinc-700',
              compact ? 'text-xs' : 'text-sm',
              !handle.name && 'font-medium',
            )}
          >
            {displayValue}
          </div>

          {/* Notes */}
          {!compact && handle.notes && (
            <div className="text-xs text-zinc-600 mt-1 italic">
              &ldquo;{handle.notes}&rdquo;
            </div>
          )}

          {/* Sources */}
          {showSources && handle.sources && handle.sources.length > 0 && (
            <div className="mt-2">
              <EnrichmentSourcesIndicator
                sources={handle.sources}
                compact={compact}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
